import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {ITriple8ConnectionFactory} from "./triple8-connection-factory.inteface";
import {ITriple8Connection} from "./triple8-connection.inteface";
import {delay} from "../../../../utils/delay";
import {Check, TimeSpan} from "@sparkware/app-utils";
import {FirebaseAnalyticsReporter} from "../firebase-analytics-reporter";

interface ITriple8ConnectResult {
    connection: ITriple8Connection;
    token: string;
}

export class Triple8ConnectionFactory {
    constructor(private readonly services: IServiceFactoryExtended,
                private readonly createNewFirebaseToken: () => Promise<string>) {
        this.analytics = new FirebaseAnalyticsReporter(services);
    }

    private _getConnectionPromise: Promise<ITriple8ConnectResult> | null = null;
    protected readonly analytics: FirebaseAnalyticsReporter;

    async getConnection(): Promise<ITriple8Connection> {
        if (!this._getConnectionPromise) {
            this._getConnectionPromise = this._createFirebaseConnection();
        }

        let result = await this._getConnectionPromise;
        return result.connection;
    }

    async getToken(): Promise<string> {
        if (!this._getConnectionPromise) {
            this._getConnectionPromise = this._createFirebaseConnection();
        }

        let result = await this._getConnectionPromise;
        return result.token;
    }

    private _isDisposed: boolean = false;
    async dispose(): Promise<void> {
        this._isDisposed = true;
        if(Triple8ConnectionFactory._getConnectionFactoryObjectPromise) {
            await (await Triple8ConnectionFactory._getConnectionFactoryObjectPromise).logOut();
        }
    }

    private async _createFirebaseConnection(): Promise<ITriple8ConnectResult> {

        const connect = async () => {
            await this.services.user.waitForAutoLogin();
            const connectionFactory = await this._getConnectionFactoryObject();
            const token = await this.createNewFirebaseToken();

            const connection = await connectionFactory.createWithCallback(token, (data) => this._enhanceAndReportFirebaseEvent(data), {} );
            return {
                connection: connection,
                token: token
            };
        }

        let retryCount = 0;
        while(!this._isDisposed) {
            try {
                const cnn = await connect();
                this.analytics.sendConnectionSuccess();
                return cnn;
            } catch (err) {
                this.services.logger.error('Failed to connect to firebase', err);
                retryCount++;
                if(retryCount <= 3) {
                    this.analytics.sendConnectionFail(retryCount.toString(), JSON.stringify(err));
                }
                await delay(TimeSpan.fromSeconds(3));
            }
        }

    }

    private static _getConnectionFactoryObjectPromise: Promise<ITriple8ConnectionFactory> | null = null;
    private async _getConnectionFactoryObject():  Promise<ITriple8ConnectionFactory> {
        if(!Triple8ConnectionFactory._getConnectionFactoryObjectPromise) {
            Triple8ConnectionFactory._getConnectionFactoryObjectPromise = this.services.scriptLoader.loadScript({
               url: this.services.configuration.servicesConfigs.PlayerMessages.Firebase.libUrl,
               tagId: 'triple8MessagingLibrary'
           }).then(() => this.services.window.getGlobalProperty('triple8Messaging'))
             .catch(err => {
                 this.services.logger.error('Failed to load triple8 lib javascript', err);
                 Triple8ConnectionFactory._getConnectionFactoryObjectPromise = null;
                 throw err;
             })
        }

        return await Triple8ConnectionFactory._getConnectionFactoryObjectPromise;
    }

    private async _enhanceAndReportFirebaseEvent(data: any): Promise<void> {
        try {
            if(!Check.isObject(data)){
                console.log("Failed on call to _enhanceAndReportEvent. The data parameter is not an object");
                return;
            }

            if(!data["eventHeader"] || !Check.isObject(data["eventHeader"])){
                console.log("Failed on call to _enhanceAndReportEvent. The data object does not contain a valid eventHeader object");
                return;
            }
            Object.assign(data["eventHeader"],{target:"platform-firebase"});

            const sessionData = {
                globalSessionID: this.services.user.userData.initialSessionId,
                playerSessionID: this.services.user.userData.sessionId
            }
            const userData = {
                cid: this.services.user.cid,
                brand: this.services.sdk.getSkin().getBrandName(),
                brandId: this.services.sdk.getBrandID()
            }
            const bakedEvent: any = Object.assign({userData:userData,sessionData:sessionData},data);
            const storageUrl = this.services.configuration.servicesConfigs.StructuredLogging.storageUrl;
            await this.services.http.post(storageUrl, {body : bakedEvent, keepalive : true});
        }
        catch (err) {
            console.log("Failed on call to _enhanceAndReportEvent. Error: " + err);
        }
    }
}